<template>
  <div class="usage-records">
    <!-- LOADING -->
    <div class="loading-mask" v-if="loading">
      <div class="loading-spinner"></div>
      <span class="loading-text">Loading...</span>
    </div>

    <!-- HEADER -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">Usage Records</h1>
        <span class="period-badge">{{ periodLabel }}</span>
      </div>
      <div class="header-actions">
        <button class="btn-refresh" @click="fetchData" :disabled="loading">
          <span :class="{ spin: loading }">↺</span> Refresh
        </button>
        <button class="btn-exit" @click="exitToSubscriptions">Exit</button>
      </div>
    </div>

    <!-- ERROR -->
    <div class="error-bar" v-if="error">⚠ {{ error }}</div>

    <!-- STATS -->
    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-label">Total Records</div>
        <div class="stat-val">{{ stats.total.toLocaleString() }}</div>
        <div class="stat-sub">Last 30 days</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Active Users</div>
        <div class="stat-val">{{ stats.users }}</div>
        <div class="stat-sub">Unique users</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Total Points</div>
        <div class="stat-val">{{ formatAmount(stats.totalAmount) }}</div>
        <div class="stat-sub">Avg {{ stats.avgAmount.toFixed(1) }} / req</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Input Tokens</div>
        <div class="stat-val">{{ formatAmount(stats.totalInputTokens) }}</div>
        <div class="stat-sub">Avg {{ stats.avgInputTokens.toFixed(1) }} / req</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Output Tokens</div>
        <div class="stat-val">{{ formatAmount(stats.totalOutputTokens) }}</div>
        <div class="stat-sub">Avg {{ stats.avgOutputTokens.toFixed(1) }} / req</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Feature Types</div>
        <div class="stat-val">{{ stats.bizCount }}</div>
        <div class="stat-sub">Distinct features</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Top Feature</div>
        <div class="stat-val stat-val--sm">{{ stats.topBiz || '—' }}</div>
        <div class="stat-sub">Most called</div>
      </div>
    </div>

    <!-- FILTERS -->
    <div class="filters">
      <div class="search-wrap">
        <span class="search-icon">⌕</span>
        <input v-model="search" class="input-search" placeholder="Search username / feature / input..." @input="currentPage = 1" />
      </div>
      <select class="filter-select" v-model="filterBiz" @change="currentPage = 1">
        <option value="">All Features</option>
        <option v-for="b in bizOptions" :key="b" :value="b">{{ b }}</option>
      </select>
      <select class="filter-select" v-model="filterUser" @change="currentPage = 1">
        <option value="">All Users</option>
        <option v-for="u in userOptions" :key="u" :value="u">{{ u }}</option>
      </select>
      <select class="filter-select" v-model="sortKey">
        <option value="createdTime_desc">Time ↓</option>
        <option value="createdTime_asc">Time ↑</option>
        <option value="amount_desc">Points ↓</option>
        <option value="amount_asc">Points ↑</option>
      </select>
      <span class="filter-count">{{ filtered.length }} records</span>
    </div>

    <!-- TABLE -->
    <div class="table-wrap">
      <table>
        <colgroup>
          <col :style="{ width: columnWidths.time + 'px' }" />
          <col :style="{ width: columnWidths.user + 'px' }" />
          <col :style="{ width: columnWidths.feature + 'px' }" />
          <col :style="{ width: columnWidths.model + 'px' }" />
          <col :style="{ width: columnWidths.points + 'px' }" />
          <col :style="{ width: columnWidths.inputTokens + 'px' }" />
          <col :style="{ width: columnWidths.outputTokens + 'px' }" />
          <col :style="{ width: columnWidths.preview + 'px' }" />
        </colgroup>
        <thead>
          <tr>
            <th>
              <span class="th-label">Time (UTC+8)</span>
              <span class="col-resizer" @mousedown="startResize($event, 'time')"></span>
            </th>
            <th>
              <span class="th-label">User</span>
              <span class="col-resizer" @mousedown="startResize($event, 'user')"></span>
            </th>
            <th>
              <span class="th-label">Feature</span>
              <span class="col-resizer" @mousedown="startResize($event, 'feature')"></span>
            </th>
            <th>
              <span class="th-label">Model</span>
              <span class="col-resizer" @mousedown="startResize($event, 'model')"></span>
            </th>
            <th>
              <span class="th-label">Points</span>
              <span class="col-resizer" @mousedown="startResize($event, 'points')"></span>
            </th>
            <th>
              <span class="th-label">Input Tokens</span>
              <span class="col-resizer" @mousedown="startResize($event, 'inputTokens')"></span>
            </th>
            <th>
              <span class="th-label">Output Tokens</span>
              <span class="col-resizer" @mousedown="startResize($event, 'outputTokens')"></span>
            </th>
            <th>
              <span class="th-label">Content Preview</span>
              <span class="col-resizer" @mousedown="startResize($event, 'preview')"></span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="paged.length === 0">
            <td colspan="8" class="empty-cell">No data</td>
          </tr>
          <tr v-for="row in paged" :key="row.Id">
            <td class="cell-time">{{ formatTime(row.CreatedTime) }}</td>
            <td>
              <div class="cell-username">{{ row.UserName || '—' }}</div>
            </td>
            <td><span class="biz-tag">{{ row.BizName }}</span></td>
            <td class="cell-time">{{ row.Model || '—' }}</td>
            <td>
              <span class="cell-amount" :class="{ 'cell-amount--high': row.Amount > 1000 }">
                {{ row.Amount.toLocaleString() }}
              </span>
            </td>
            <td class="cell-time">{{ getInputTokens(row).toLocaleString() }}</td>
            <td class="cell-time">{{ getOutputTokens(row).toLocaleString() }}</td>
            <td class="cell-io">
              <template v-if="row.Input || row.Output">
                <div class="io-preview">{{ row.Input ? `In: ${row.Input}` : '' }}{{ row.Input && row.Output ? '  |  ' : '' }}{{ row.Output ? `Out: ${row.Output}` : '' }}</div>
                <span v-if="(row.Input && row.Input.length > 80) || (row.Output && row.Output.length > 80)" class="expand-btn"
                  @click="openModal(row)">Details</span>
              </template>
              <span v-else class="empty-val">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- PAGINATION -->
    <div class="pagination" v-if="filtered.length > 0">
      <span class="page-info">Page {{ currentPage }} / {{ totalPages }}</span>
      <div class="page-btns">
        <button class="page-btn" @click="currentPage = 1" :disabled="currentPage === 1">«</button>
        <button class="page-btn" @click="currentPage--" :disabled="currentPage === 1">‹</button>
        <button v-for="p in visiblePages" :key="p" class="page-btn" :class="{ active: p === currentPage }"
          @click="currentPage = p">{{ p }}</button>
        <button class="page-btn" @click="currentPage++" :disabled="currentPage === totalPages">›</button>
        <button class="page-btn" @click="currentPage = totalPages" :disabled="currentPage === totalPages">»</button>
      </div>
      <select class="filter-select" v-model.number="pageSize" @change="currentPage = 1">
        <option :value="20">20 / page</option>
        <option :value="50">50 / page</option>
        <option :value="100">100 / page</option>
      </select>
    </div>

    <!-- DETAIL MODAL -->
    <div class="modal-mask" v-if="modal" @click.self="modal = null">
      <div class="modal-box">
        <div class="modal-head">
          <div>
            <div class="modal-title">{{ modal.BizName }} · Details</div>
            <div class="modal-meta">
              <span class="meta-chip">{{ modal.UserName }}</span>
              <span class="meta-chip">{{ shortId(modal.UserId) }}</span>
              <span class="meta-chip">{{ formatTime(modal.CreatedTime) }}</span>
              <span class="meta-chip">{{ modal.Amount.toLocaleString() }} pts</span>
            </div>
          </div>
          <button class="modal-close" @click="modal = null">✕</button>
        </div>
        <div class="modal-body">
          <div class="io-section">
            <div class="io-label">Input</div>
            <div class="io-content">{{ modal.Input || '(None)' }}</div>
          </div>
          <div class="io-section">
            <div class="io-label">Output</div>
            <div class="io-content">{{ modal.Output || '(None)' }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { api_get_monthly_use_logs } from '../services/subscriptionApi.js';
// ── 替换为你的真实接口地址 ──────────────────────────────────
const API_URL = '/api/usage-records'

export default {
  name: 'UsageRecords',
  data() {
    return {
      records: [],
      loading: false,
      error: '',
      search: '',
      filterBiz: '',
      filterUser: '',
      sortKey: 'createdTime_desc',
      currentPage: 1,
      pageSize: 20,
      modal: null,
      columnWidths: {
        time: 170,
        user: 170,
        feature: 140,
        model: 150,
        points: 120,
        inputTokens: 130,
        outputTokens: 130,
        preview: 360,
      },
      resizeState: {
        key: '',
        startX: 0,
        startWidth: 0,
      },
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    periodLabel() {
      const now = new Date()
      const past = new Date(now - 30 * 86400000)
      return this.fmtDate(past) + ' – ' + this.fmtDate(now)
    },
    stats() {
      const r = this.records
      const userSet = new Set(r.map(x => x.UserId))
      const bizMap = {}
      r.forEach(x => { bizMap[x.BizName] = (bizMap[x.BizName] || 0) + 1 })
      const topBiz = Object.entries(bizMap).sort((a, b) => b[1] - a[1])[0]?.[0] || ''
      const totalAmount = r.reduce((s, x) => s + x.Amount, 0)
      const totalInputTokens = r.reduce((sum, row) => sum + this.getInputTokens(row), 0)
      const totalOutputTokens = r.reduce((sum, row) => sum + this.getOutputTokens(row), 0)
      const totalTokens = totalInputTokens + totalOutputTokens
      return {
        total: r.length,
        users: userSet.size,
        totalAmount,
        avgAmount: r.length ? totalAmount / r.length : 0,
        totalInputTokens,
        totalOutputTokens,
        totalTokens,
        avgInputTokens: r.length ? totalInputTokens / r.length : 0,
        avgOutputTokens: r.length ? totalOutputTokens / r.length : 0,
        bizCount: Object.keys(bizMap).length,
        topBiz,
      }
    },
    bizOptions() {
      return [...new Set(this.records.map(r => r.BizName))].sort()
    },
    userOptions() {
      return [...new Set(this.records.map(r => r.UserName).filter(Boolean))].sort()
    },
    filtered() {
      let list = this.records
      const q = this.search.trim().toLowerCase()
      if (q) {
        list = list.filter(r =>
          (r.UserName || '').toLowerCase().includes(q) ||
          (r.BizName || '').toLowerCase().includes(q) ||
          (r.Input || '').toLowerCase().includes(q)
        )
      }
      if (this.filterBiz) list = list.filter(r => r.BizName === this.filterBiz)
      if (this.filterUser) list = list.filter(r => r.UserName === this.filterUser)

      const [key, dir] = this.sortKey.split('_')
      return [...list].sort((a, b) => {
        const va = key === 'createdTime' ? new Date(a.CreatedTime) : a.Amount
        const vb = key === 'createdTime' ? new Date(b.CreatedTime) : b.Amount
        return dir === 'asc' ? va - vb : vb - va
      })
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filtered.length / this.pageSize))
    },
    paged() {
      const s = (this.currentPage - 1) * this.pageSize
      return this.filtered.slice(s, s + this.pageSize)
    },
    visiblePages() {
      const tp = this.totalPages, cp = this.currentPage
      const start = Math.max(1, cp - 2)
      const end = Math.min(tp, start + 4)
      const pages = []
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    },
  },
  methods: {
    exitToSubscriptions() {
      this.$router.push('/subscriptions')
    },
    async fetchData() {
      this.loading = true
      api_get_monthly_use_logs().then(
        (response) => {
          if (response.data.Code === 0) {
            this.loading = false
            this.records = response.data.Result.ListData || []
            this.currentPage = 1
            this.to
            console.log('Subscription balance:', response.data)
          } else {
            this.loading = false
            console.error('Failed to get subscription balance:', response.data.Message);
          }
        },
        (error) => {
          this.loading = false
          console.error('Error fetching subscription balance:', error);
        }
      );
    },
    // UTC → UTC+8
    formatTime(utcStr) {
      if (!utcStr) return '—'
      const d = new Date(new Date(utcStr).getTime() + 8 * 3600000)
      const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
      // return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`
    },
    fmtDate(d) {
      const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    },
    formatAmount(n) {
      if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
      if (n >= 1000) return (n / 1000).toFixed(1) + 'K'
      return n.toFixed(0)
    },
    shortId(id) {
      return id && id.length > 8 ? id.substring(0, 8) + '…' : (id || '')
    },
    pickTokenField(row, keys) {
      for (const key of keys) {
        const value = row?.[key]
        const numberValue = Number(value)
        if (value !== null && value !== undefined && value !== '' && Number.isFinite(numberValue) && numberValue >= 0) {
          return numberValue
        }
      }
      return null
    },
    parseUsage(usage) {
      if (!usage) return null
      if (typeof usage === 'object') return usage
      if (typeof usage === 'string') {
        try {
          return JSON.parse(usage)
        } catch (e) {
          return null
        }
      }
      return null
    },
    countTextUnits(text) {
      const content = String(text || '').trim()
      if (!content) return 0

      const chineseChars = content.match(/[\u4e00-\u9fff]/g)
      if (chineseChars && chineseChars.length) return chineseChars.length

      const englishWords = content.match(/[A-Za-z]+(?:'[A-Za-z]+)*/g)
      return englishWords ? englishWords.length : 0
    },
    getInputTokens(row) {
      const usage = this.parseUsage(row?.Usage)
      if (usage) {
        const usageInputTokens = this.pickTokenField(usage, [
          'prompt_tokens', 'input_tokens', 'promptTokens', 'inputTokens',
        ])
        if (usageInputTokens !== null) return usageInputTokens
      }

      const tokenValue = this.pickTokenField(row, [
        'InputTokens', 'InputToken', 'inputTokens', 'inputToken',
        'PromptTokens', 'promptTokens', 'prompt_token', 'input_tokens',
      ])
      if (tokenValue !== null) return tokenValue
      return this.countTextUnits(row?.Input)
    },
    getOutputTokens(row) {
      const usage = this.parseUsage(row?.Usage)
      if (usage) {
        const usageOutputTokens = this.pickTokenField(usage, [
          'completion_tokens', 'output_tokens', 'completionTokens', 'outputTokens',
        ])
        if (usageOutputTokens !== null) return usageOutputTokens
      }

      const tokenValue = this.pickTokenField(row, [
        'OutputTokens', 'OutputToken', 'outputTokens', 'outputToken',
        'CompletionTokens', 'completionTokens', 'completion_token', 'output_tokens',
      ])
      if (tokenValue !== null) return tokenValue
      return this.countTextUnits(row?.Output)
    },
    startResize(event, key) {
      event.preventDefault()
      this.resizeState = {
        key,
        startX: event.clientX,
        startWidth: this.columnWidths[key],
      }
      window.addEventListener('mousemove', this.onResize)
      window.addEventListener('mouseup', this.stopResize)
    },
    onResize(event) {
      if (!this.resizeState.key) return
      const minWidths = {
        time: 140,
        user: 140,
        feature: 120,
        model: 120,
        points: 90,
        inputTokens: 110,
        outputTokens: 110,
        preview: 220,
      }
      const delta = event.clientX - this.resizeState.startX
      const key = this.resizeState.key
      const width = Math.max(minWidths[key] || 100, this.resizeState.startWidth + delta)
      this.columnWidths = {
        ...this.columnWidths,
        [key]: width,
      }
    },
    stopResize() {
      this.resizeState = {
        key: '',
        startX: 0,
        startWidth: 0,
      }
      window.removeEventListener('mousemove', this.onResize)
      window.removeEventListener('mouseup', this.stopResize)
    },
    openModal(row) {
      this.modal = row
    },
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.onResize)
    window.removeEventListener('mouseup', this.stopResize)
  },
  watch: {
    filtered() {
      if (this.currentPage > this.totalPages) this.currentPage = 1
    },
  },
}
</script>

<style scoped>
/* ── 变量 ── */
.usage-records {
  --bg: #0b1220;
  --surface: #121c2f;
  --surface2: #17243b;
  --border: #263754;
  --text: #e6eefb;
  --muted: #95a5c0;
  --muted2: #637694;
  --ink: #f4f8ff;
  --accent: #3ea6ff;
  --accent-soft: rgba(62, 166, 255, 0.14);
  --green: #4ade80;

  font-family: 'Plus Jakarta Sans', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-size: 14px;
  color: var(--text);
  background:
    radial-gradient(circle at 10% 10%, rgba(62, 166, 255, 0.12), transparent 40%),
    radial-gradient(circle at 90% 0%, rgba(96, 165, 250, 0.1), transparent 38%),
    var(--bg);
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* ── LOADING ── */
.loading-mask {
  position: fixed;
  inset: 0;
  background: rgba(9, 14, 25, 0.78);
  z-index: 300;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 2px solid rgba(149, 165, 192, 0.35);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-text {
  font-size: 12px;
  color: var(--muted);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spin {
  display: inline-block;
  animation: spin 0.8s linear infinite;
}

/* ── HEADER ── */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px 16px;
  background: rgba(18, 28, 47, 0.88);
  border-bottom: 1px solid var(--border);
  backdrop-filter: blur(8px);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: var(--ink);
}

.period-badge {
  font-size: 11px;
  font-family: 'IBM Plex Mono', 'Courier New', monospace;
  color: var(--muted);
  background: var(--surface2);
  border: 1px solid var(--border);
  padding: 3px 10px;
  border-radius: 999px;
}

.btn-refresh {
  background: linear-gradient(135deg, #2d8fff, #49b1ff);
  color: #ffffff;
  border: 1px solid rgba(145, 204, 255, 0.35);
  border-radius: 10px;
  padding: 7px 16px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}

.btn-refresh:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(62, 166, 255, 0.28);
}

.btn-refresh:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-exit {
  background: var(--surface2);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 7px 16px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.btn-exit:hover {
  border-color: var(--accent);
  color: var(--accent);
}

/* ── ERROR ── */
.error-bar {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(248, 113, 113, 0.35);
  color: #fecaca;
  padding: 10px 28px;
  font-size: 12px;
}

/* ── STATS ── */
.stats-row {
  display: flex;
  gap: 10px;
  padding: 10px 28px;
  border-bottom: 1px solid var(--border);
}

.stat-card {
  flex: 1;
  background: rgba(18, 28, 47, 0.9);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 16px 20px;
}

.stat-label {
  font-size: 10px;
  font-family: 'IBM Plex Mono', monospace;
  color: var(--muted);
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.stat-val {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: var(--ink);
}

.stat-val--sm {
  font-size: 15px;
}

.stat-sub {
  font-size: 11px;
  color: var(--muted);
  margin-top: 3px;
}

/* ── FILTERS ── */
.filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 14px 28px;
  border-bottom: 1px solid var(--border);
  background: transparent;
}

.search-wrap {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 300px;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted2);
  font-size: 15px;
  pointer-events: none;
}

.input-search {
  width: 100%;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 8px 12px 8px 32px;
  font-size: 13px;
  color: var(--text);
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.input-search:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(62, 166, 255, 0.18);
}

.filter-select {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 13px;
  color: var(--text);
  outline: none;
  cursor: pointer;
  min-width: 130px;
}

.filter-count {
  font-size: 11px;
  color: var(--muted);
  margin-left: auto;
  font-family: monospace;
}

/* ── TABLE ── */
.table-wrap {
  flex: 1;
  min-height: 0;
  padding: 16px 28px 0;
  overflow: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  background: rgba(18, 28, 47, 0.92);
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid var(--border);
}

thead {
  background: var(--surface2);
  border-bottom: 1px solid var(--border);
}

th {
  text-align: left;
  padding: 10px 14px;
  font-size: 10px;
  font-weight: 500;
  font-family: 'IBM Plex Mono', monospace;
  letter-spacing: 1px;
  color: var(--muted);
  white-space: nowrap;
  position: relative;
}

.th-label {
  display: inline-block;
  padding-right: 10px;
}

.col-resizer {
  position: absolute;
  top: 0;
  right: -4px;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  user-select: none;
}

.col-resizer::after {
  content: '';
  position: absolute;
  right: 3px;
  top: 20%;
  bottom: 20%;
  width: 1px;
  background: rgba(149, 165, 192, 0.4);
}

.col-resizer:hover::after {
  background: var(--accent);
}

td {
  padding: 11px 14px;
  border-bottom: 1px solid rgba(38, 55, 84, 0.75);
  vertical-align: top;
  line-height: 1.4;
}

tr:last-child td {
  border-bottom: none;
}

tr:hover td {
  background: rgba(23, 36, 59, 0.9);
}

.cell-time {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  color: var(--muted);
  white-space: nowrap;
}

.cell-username {
  font-weight: 600;
  font-size: 13px;
}

.cell-uid {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  color: var(--muted2);
  margin-top: 2px;
}

.biz-tag {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  background: var(--accent-soft);
  color: var(--accent);
  white-space: nowrap;
}

.cell-amount {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 13px;
  font-weight: 500;
  color: var(--green);
}

.cell-amount--high {
  color: var(--accent);
}

.cell-io {
  max-width: 240px;
}

.io-preview {
  font-size: 12px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.expand-btn {
  font-size: 11px;
  color: var(--accent);
  cursor: pointer;
  margin-top: 2px;
  display: inline-block;
}

.expand-btn:hover {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.empty-val {
  color: var(--muted2);
  font-size: 12px;
}

.empty-cell {
  text-align: center;
  padding: 48px;
  color: var(--muted);
  font-size: 13px;
}

/* ── PAGINATION ── */
.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 28px;
  border-top: 1px solid var(--border);
  background: rgba(18, 28, 47, 0.95);
  margin: 0 28px;
  border-radius: 0 0 14px 14px;
  border: 1px solid var(--border);
  border-top: none;
}

.page-info {
  font-size: 12px;
  color: var(--muted);
  font-family: monospace;
}

.page-btns {
  display: flex;
  gap: 4px;
}

.page-btn {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 11px;
  font-family: monospace;
  cursor: pointer;
  color: var(--text);
  transition: all 0.15s;
}

.page-btn:hover:not(:disabled) {
  border-color: var(--accent);
  color: var(--accent);
}

.page-btn.active {
  background: linear-gradient(135deg, #2d8fff, #49b1ff);
  color: #fff;
  border-color: #63beff;
}

.page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* ── MODAL ── */
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(8, 13, 23, 0.64);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.modal-box {
  background: rgba(18, 28, 47, 0.98);
  border-radius: 14px;
  border: 1px solid var(--border);
  width: 100%;
  max-width: 640px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 24px 70px rgba(7, 11, 21, 0.62);
}

.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  background: var(--surface);
}

.modal-title {
  font-weight: 700;
  font-size: 15px;
}

.modal-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.meta-chip {
  font-family: monospace;
  font-size: 10px;
  background: var(--surface2);
  border: 1px solid var(--border);
  padding: 2px 8px;
  border-radius: 4px;
  color: var(--muted);
}

.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--muted);
  font-size: 18px;
}

.modal-close:hover {
  color: var(--text);
}

.modal-body {
  padding: 20px 24px;
}

.io-section {
  margin-bottom: 18px;
}

.io-section:last-child {
  margin-bottom: 0;
}

.io-label {
  font-family: monospace;
  font-size: 10px;
  letter-spacing: 1px;
  color: var(--muted);
  text-transform: uppercase;
  margin-bottom: 8px;
}

.io-content {
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 200px;
  overflow-y: auto;
}

/* ── scrollbar ── */
::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(149, 165, 192, 0.55);
  border-radius: 999px;
}
</style>
